'use client';

/**
 * A one-shot, imperative Turnstile challenge for the mint call sites that are
 * a bare event handler rather than a mounted React surface.
 *
 * `TurnstileWidget` covers the surfaces that can keep an invisible widget
 * mounted (the splash panel, the `/w` viewer). Three `startGuest` call sites
 * cannot: the create FAB and the rail sign-in item are menu rows built by
 * `CreateMenu`/`RailItem`, and `makeFetchWithGuestMint` is not a component at
 * all. All three shipped with no token, which in production is an
 * unconditional 403 `turnstile_required` from `/api/auth/guest` — the first
 * write on a public canvas silently reverted, and the FAB dead-ended on
 * `/signup`. All three are driven by a real gesture, so running the challenge
 * inline on the gesture costs nothing.
 *
 * The Cloudflare script and the `window.turnstile` typing live here so the
 * component and this path share ONE loader; two loaders would race and inject
 * the script twice.
 */
import { getRuntimeConfig } from '@/lib/runtime-config';

declare global {
  // eslint-disable-next-line no-var
  var turnstile:
    | {
        render: (el: HTMLElement, opts: Record<string, unknown>) => string;
        execute: (idOrEl: string | HTMLElement, opts?: Record<string, unknown>) => void;
        remove: (id: string) => void;
        reset: (id?: string) => void;
      }
    | undefined;
}

/**
 * Sent as the widget's `action` and asserted by `verifyTurnstile`. Duplicated
 * from `lib/auth/turnstile.ts` on purpose: importing that module here would
 * pull secret-reading server code into a browser bundle.
 */
export const TURNSTILE_ACTION = 'guest-mint';

/** How long a single challenge may wait for Cloudflare before giving up. Long
 *  enough for a human to solve a visible challenge; an abandoned one no
 *  longer strands the caller's button either way, because an interactive
 *  challenge can be dismissed (backdrop click, Escape) the moment it appears. */
export const EXECUTE_TIMEOUT_MS = 60_000;

/** True when a site key is configured, so callers know to require a token. */
export function turnstileSiteKey(): string | null {
  return getRuntimeConfig().turnstileSiteKey || null;
}

let scriptPromise: Promise<void> | null = null;
export function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Run the bot challenge now and resolve a token, or null if there is no site
 * key (dev), the script is blocked, or the challenge is never answered.
 *
 * Null is a normal outcome, not an exception: callers pass it straight to
 * `startGuest`, which lets the server take the decision (it allows in dev
 * where no secret is set, and refuses in production) rather than the client
 * guessing.
 */
export async function executeTurnstile(timeoutMs = EXECUTE_TIMEOUT_MS): Promise<string | null> {
  const siteKey = turnstileSiteKey();
  if (!siteKey || typeof window === 'undefined') return null;
  try {
    await loadTurnstileScript();
  } catch {
    return null; // ad blockers routinely block challenges.cloudflare.com
  }
  const api = window.turnstile;
  if (!api) return null;

  const host = document.createElement('div');
  // Zero-sized until Cloudflare asks for the visitor: an INTERACTIVE challenge
  // inside a hidden box can never be solved, so the promise would sit there to
  // the timeout and the gesture would dead-end with no explanation.
  host.style.cssText = 'position:fixed;width:0;height:0;overflow:hidden';
  document.body.appendChild(host);

  return new Promise<string | null>((resolve) => {
    let widgetId: string | undefined;
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // The visitor's way out of an interactive challenge. Once Cloudflare
    // escalates, the host is a full-viewport backdrop with no close control,
    // and without these the only exit was the timeout: a minute parked behind
    // a dimmed page for anyone who would rather not solve it. Both resolve
    // null, which the caller already treats as "no token" (sign-up form
    // instead of the guest mint), so declining costs the shortcut, not the
    // page.
    const onBackdropClick = (e: MouseEvent) => {
      if (e.target === host) settle(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') settle(null);
    };
    const settle = (token: string | null) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      host.removeEventListener('click', onBackdropClick);
      document.removeEventListener('keydown', onKeyDown);
      try {
        if (widgetId) api.remove(widgetId);
      } catch {
        /* already gone */
      }
      host.remove();
      resolve(token);
    };
    timer = setTimeout(() => settle(null), timeoutMs);
    try {
      widgetId = api.render(host, {
        sitekey: siteKey,
        appearance: 'execute',
        execution: 'execute',
        action: TURNSTILE_ACTION,
        theme: 'auto',
        callback: (t: string) => settle(t),
        'error-callback': () => settle(null),
        'timeout-callback': () => settle(null),
        'before-interactive-callback': () => {
          // Cloudflare wants the visitor. Bring the box on screen, centred over
          // a dim backdrop, so there is something to click, and give the
          // backdrop itself the two exits a modal is expected to have.
          host.style.cssText =
            'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.45)';
          host.setAttribute('role', 'dialog');
          host.setAttribute('aria-modal', 'true');
          host.addEventListener('click', onBackdropClick);
          document.addEventListener('keydown', onKeyDown);
        },
      });
      if (!widgetId) {
        settle(null);
        return;
      }
      api.execute(widgetId);
    } catch {
      settle(null);
    }
  });
}
